
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { UIProvider } from "@/context/UIContext";
import { WellnessQueryProvider } from "@/context/WellnessContext";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Footer } from "@/components/Footer";
import VitaChat from "./pages/VitaChat";
import Index from "./pages/Index";
import Pricing from "./pages/Pricing";
import Auth from "./pages/Auth";
import SetupPassword from "./pages/SetupPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import CheckoutRedirect from "./pages/CheckoutRedirect";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import NotFound from "./pages/NotFound";
import { useAuth } from "@/context/AuthContext";

// Create a component to handle route changes
const RouteChangeHandler = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  
  useEffect(() => {
    // Check if URL contains www subdomain and redirect if needed
    if (window.location.hostname.startsWith('www.')) {
      const nonWwwUrl = window.location.href.replace('www.', '');
      window.location.href = nonWwwUrl;
    }
    
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location]);
  
  return (
    <>
      {children}
      <Footer />
    </>
  );
};

// Create a component to handle the home route
const HomeRouteHandler = () => {
  const { user } = useAuth();
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Index />;
};

const queryClient = new QueryClient();

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomeRouteHandler />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<Auth />} />
      <Route path="/auth/setup-password" element={<SetupPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      
      {/* Checkout redirect routes */}
      <Route path="/checkout/success" element={<CheckoutRedirect />} />
      <Route path="/checkout/redirect" element={<CheckoutRedirect />} />
      
      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/vita" element={<VitaChat />} />
      </Route>
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <UIProvider>
        <AuthProvider>
          <WellnessQueryProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <RouteChangeHandler>
                <AppRoutes />
              </RouteChangeHandler>
            </TooltipProvider>
          </WellnessQueryProvider>
        </AuthProvider>
      </UIProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
